import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { createChannelUrl } from "../constants";

// Sendbird error code for "Channel not found"
const SENDBIRD_CHANNEL_NOT_FOUND_ERROR = 400_111;

const createChannelRequestSchema = z.object({
  facilityId: z.string(),
  agentId: z.string(),
});

const createChannelResponseSchema = z.any(); // API doesn't return specific data

interface EnsureChatChannelParams {
  workplaceId: string;
  workerId: string;
}

/**
 * Mutation hook to ensure a chat channel exists between workplace and worker.
 * Creates the channel if it doesn't exist yet via POST /chat/channels.
 * Invalidates the chat channel query cache after successful creation.
 *
 * @example
 * ```tsx
 * const { mutate: ensureChannel, isPending } = useEnsureChatChannel();
 *
 * ensureChannel({
 *   workplaceId: "workplace123",
 *   workerId: "worker456",
 * });
 * ```
 */
export function useEnsureChatChannel() {
  const queryClient = useQueryClient();
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;

  return useMutation({
    mutationFn: async ({ workplaceId, workerId }: EnsureChatChannelParams) => {
      if (!sdk) {
        throw new Error("Sendbird SDK not initialized");
      }

      const channelUrl = createChannelUrl(workplaceId, workerId);

      // Check if channel already exists
      try {
        const existingChannel = await sdk.groupChannel.getChannel(channelUrl);
        return existingChannel;
      } catch (error) {
        // Channel doesn't exist, create it
        if (
          error &&
          typeof error === "object" &&
          "code" in error &&
          error.code === SENDBIRD_CHANNEL_NOT_FOUND_ERROR
        ) {
          await post({
            url: `${environmentConfig.REACT_APP_BASE_API_URL}/chat/channels`,
            data: {
              facilityId: workplaceId,
              agentId: workerId,
            },
            requestSchema: createChannelRequestSchema,
            responseSchema: createChannelResponseSchema,
          });

          // Fetch the newly created channel
          const newChannel = await sdk.groupChannel.getChannel(channelUrl);
          return newChannel;
        }
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate the channel query to refetch with the new channel
      void queryClient.invalidateQueries({
        queryKey: ["chat", "channel", variables.workplaceId, variables.workerId],
      });
    },
    meta: {
      userErrorMessage: "Failed to create chat channel",
    },
  });
}
